import { getProfileIdModelUtil } from '../../../../models/utils/get-profile-id'
import { getObjectUtil, isPlainObjectUtil, logUtil } from 'packages/src'

import { deleteApi } from '../../../api/utils/delete'
import { postApi } from '../../../api/utils/post'
import { putApi } from '../../../api/utils/put'
import openErrorBannerAction from '../../../banner/actions/open-error'
import sendErrorAction from '../../../error/actions/send'
import getMessageErrorUtil from '../../../error/utils/get-message'
import { EXERCISE_CLASS_NAME } from '../../../exercise'
import getJwtSelect from '../../../jwt/selectors/get'
import addRequestNetworkAction from '../../../network/actions/add-request'
import deleteRequestNetworkAction from '../../../network/actions/delete-request'
import incrementRetryCountNetworkAction from '../../../network/actions/increment-retry-count'
import updateIsSendingDataNetworkAction from '../../../network/actions/update-is-sending-data'
import { NETWORK_ACTION, NETWORK_PATH, NETWORK_REQUEST_PATH } from '../../../network/constants'
import getRequestsNetworkSelect from '../../../network/selectors/get-requests'
import isConnectedNetworkSelect from '../../../network/selectors/is-connected'
import { getStatusNetworkUtil } from '../../../network/utils/get-status'
import { getCurrentOrCustomerIdProfileSelect } from '../../../profile/redux/selectors/get-current-or-customer-id'
import isAdminProfileSelect from '../../../profile/redux/selectors/is-admin'
import afterDeleteReduxAction from '../after-delete'
import createReduxAction from '../create'
import setReduxAction from '../set'
import REDUX_ACTION from '../../constants/action'
import { REDUX_MODEL_KEY } from '../../constants/key'
import REDUX_OPTION from '../../constants/option'
import REDUX_STATUS from '../../constants/status'
import beforeSaveReduxHook from '../../hooks/before-save'
import isSavingReduxSelector from '../../selectors/is-saving'
import { getActionStrReduxUtil } from '../../utils/get-action-str'
import { getSavableAssociationKeysReduxUtil } from '../../utils/get-associations-keys'
import getClassNameReduxUtil from '../../utils/get-class-name'
import getInstanceReduxUtil from '../../utils/get-instance'
import getSavableDataReduxUtil from '../../utils/get-savable-data'
import getUpdatedDataReduxUtil from '../../utils/get-updated-data'
import { getValueReduxUtil } from '../../utils/get-value'
import { hasChangedReduxUtil } from '../../utils/has-changed'
import isInstanceReduxUtil from '../../utils/is-instance'
import isNewReduxUtil from '../../utils/is-new'
import isTemporaryIdReduxUtil from '../../utils/is-temporary-id'
import { WORKOUT_EXERCISE_CLASS_NAME } from '../../../workout-exercise/model/constants'
import { WORKOUT_SECTION_CLASS_NAME } from '../../../workout-section'
import setUtil from '../../../../utils/set'
import { joinPathsStringUtil } from '../../../../utils/string/get-joined-paths'

const METHOD_FN = {
  POST: postApi,
  PUT: putApi,
  DELETE: deleteApi
}

/**
 * Sets the given `path` status as success and set the data.
 * @param {Object} action
 *        {number} type
 *        {Object} action.payload
 *        {string} action.payload.path
 *        {Object} [action.payload.data]
 */
function saveSuccessReduxAction (action) {
  return {
    type: action.type + '_SUCCESS',
    payload: action.payload,
    reduxType: REDUX_ACTION.SAVE_SUCCESS
  }
}

/**
 * Sets the given `path` status as error and set the error.
 * @param {Object} action
 *        {number} type
 *        {Object} action.payload
 *        {string} action.payload.path
 *        {Object} action.payload.error
 */
function saveErrorReduxAction (action) {
  return (dispatch) => {
    dispatch(sendErrorAction(action.payload.error))
    dispatch({
      type: action.type + '_ERROR',
      reduxType: REDUX_ACTION.SAVE_ERROR,
      payload: action.payload
    })
  }
}

/**
 * Sets the given `path` status as saving.
 * @param {Object} action
 *        {string} action.type
 *        {Object} action.payload
 *        {string} action.payload.path
 */
function savingReduxAction (action) {
  return (dispatch) => {
    dispatch({
      type: action.type,
      payload: action.payload,
      reduxType: REDUX_ACTION.SAVE
    })
  }
}

function getDeepUpdatedInstance (instance, state) {
  const associationPointerKeys = getSavableAssociationKeysReduxUtil(instance)
  if (associationPointerKeys.length) {
    instance = associationPointerKeys.reduce((newInstance, associationPointerKey) => {
      const associationPointer = getValueReduxUtil(newInstance,
        associationPointerKey
      )
      // If the instance has no association to save, no-op
      // e.g. Post.attachment is not always necessary
      if (!associationPointer) {
        return newInstance
      }

      if (Array.isArray(associationPointer)) {
        newInstance = setUtil(newInstance,
          joinPathsStringUtil(REDUX_MODEL_KEY.CHANGED, associationPointerKey),
          associationPointer.reduce((arr, associationPointerElem, index) => {
            const assoc = getInstanceReduxUtil(state, associationPointerElem)
            if (!assoc) {
              logUtil('').warn(
                `association with key ${associationPointerKey} with index ${index} is not defined: `,
                newInstance
              )
              return arr
            }
            const deepAssoc = getDeepUpdatedInstance(assoc, state)
            arr.push(deepAssoc)
            return arr
          }, [])
        )
      } else {
        const assoc = getInstanceReduxUtil(state, associationPointer)
        if (!assoc) {
          logUtil('').warn(
            `association with key ${associationPointerKey} is not defined: `,
            instance
          )
          return newInstance
        }
        newInstance = setUtil(newInstance,
          joinPathsStringUtil(REDUX_MODEL_KEY.CHANGED, associationPointerKey),
          getDeepUpdatedInstance(assoc, state)
        )
      }
      return newInstance
    }, instance)
  }
  return getSavableDataReduxUtil(instance)
}

function sendRemoteInstanceReduxAction (action) {
  return async (dispatch, getState) => {
    const { payload } = action
    const {
      domainListPath,
      endpoint,
      path,
      method,
      fetchOptions,
      options
    } = payload

    const instance = getObjectUtil(getState(), path)
    if (!instance) {
      logUtil('').warn(`[noop] instance at path ${path} does not exist: `, action)
      return
    }

    if (!endpoint) {
      logUtil('').warn('[noop] endpoint undefined: ', action)
      return
    }

    dispatch(savingReduxAction({
      type: action.type,
      payload: {
        path
      }
    }))

    try {
      const jwt = getJwtSelect(getState())
      let dataToSave = getDeepUpdatedInstance(instance, getState())
      if (!dataToSave) {
        logUtil('sendRemoteInstanceReduxAction').warn(`dataToSave is empty for path ${path}`)
        return
      }
      dataToSave = beforeSaveReduxHook(dataToSave)
      // logUtil'sendRemoteInstanceReduxAction.debug(`dataToSave
      // ${JSON.stringify(dataToSave)}`)
      let methodFn
      if (method) {
        methodFn = METHOD_FN[method]
      } else if (!isNewReduxUtil(instance)) {
        methodFn = putApi
      } else {
        methodFn = postApi
      }

      const response = await methodFn({
        endpoint,
        data: dataToSave,
        jwt,
        fetchOptions
      })
      if (!response) {
        logUtil('sendRemoteInstanceReduxAction').error('no response for data: ',
          dataToSave
        )
        return
      }
      // logUtil'sendRemoteInstanceReduxAction.debug(`response
      // ${JSON.stringify(response)}`)
      const responseData = response.data
      if (!isPlainObjectUtil(responseData)) {
        logUtil('sendRemoteInstanceReduxAction').warn('responseData is not an object ',
          response
        )
        return
      }
      const responseDataVal = Object.values(response.data)[0]
      if (responseDataVal) {
        dispatch(createReduxAction({
          type: action.type + '_CREATE_AFTER_SAVE',
          payload: {
            domainListPath,
            data: {
              data: responseDataVal,
              status: REDUX_STATUS.SUCCESS
            },
            existingInstance: instance
          }
        }))
      }

      if (instance) {
        dispatch(saveSuccessReduxAction({
          type: action.type,
          payload: {
            path
          }
        }))
      }

      const responseExtraData = response.extraData
      if (isPlainObjectUtil(responseExtraData) &&
        Object.keys(responseExtraData).length) {
        Object.keys(responseExtraData)
          .forEach(key => {
            const value = responseExtraData[key]
            if (!value) {
              return
            }
            dispatch(createReduxAction({
              type: `${getActionStrReduxUtil(getActionStrReduxUtil(action.type,
                'CREATE'
              ), key)}_AFTER_SAVE_EXTRA_DATA`,
              payload: {
                data: Array.isArray(value)
                  ? value
                  : { data: value },
                status: REDUX_STATUS.SUCCESS
              }
            }))
          })
      }

      if (!responseDataVal && methodFn === METHOD_FN.DELETE) {
        // Delete the instance
        dispatch(afterDeleteReduxAction({
          type: action.type,
          payload: {
            instance
          }
        }))
      }

      return response
    } catch (error) {
      dispatch(saveErrorReduxAction({
        type: action.type,
        payload: {
          path,
          error
        }
      }))
      if (options[REDUX_OPTION.SHOW_ERROR]) {
        dispatch(openErrorBannerAction({
          message: getMessageErrorUtil(error)
        }))
      } else {
        throw error
      }
    }
  }
}

function sendRemoteDataReduxAction (action) {
  return async (dispatch, getState) => {
    const { payload } = action
    const {
      endpoint,
      data,
      path,
      options,
      method
    } = payload
    if (path) {
      dispatch(savingReduxAction({
        type: action.type,
        payload: {
          path
        }
      }))
    }
    try {
      const jwt = getJwtSelect(getState())
      const methodFn = method
        ? METHOD_FN[method]
        : postApi
      dispatch(updateIsSendingDataNetworkAction(true))
      const response = await methodFn({
        endpoint,
        data,
        jwt
      })
      dispatch(updateIsSendingDataNetworkAction())
      if (!response) {
        logUtil('').warn('no data for endpoint: ', endpoint)
        return
      }
      // If it's saving a single instance, update the instance
      const domainOrInstance = getObjectUtil(getState(), path)
      const responseData = response.data
      if (!isPlainObjectUtil(responseData)) {
        logUtil('').warn('sendRemoteDataReduxAction: responseData is not an object ',
          response.data
        )
        return
      }
      const responseDataVal = Array.isArray(responseData)
        ? responseData[0]
        : Object.values(responseData)[0]

      if (isInstanceReduxUtil(domainOrInstance) && responseDataVal) {
        dispatch(createReduxAction({
          type: action.type + '_UPDATE_AFTER_SAVE',
          payload: {
            data: {
              data: responseDataVal,
              status: REDUX_STATUS.SUCCESS
            },
            existingInstance: domainOrInstance
          }
        }))
      }

      if (path) {
        dispatch(saveSuccessReduxAction({
          type: action.type,
          payload: {
            path,
            options
          }
        }))
      }
      return response
    } catch (error) {
      dispatch(updateIsSendingDataNetworkAction())
      if (path) {
        dispatch(saveErrorReduxAction({
          type: action.type,
          payload: {
            path,
            error
          }
        }))
      }
      dispatch(openErrorBannerAction({
        message: getMessageErrorUtil(error)
      }))
      throw error
    }
  }
}

function sendRemoteReduxAction (action) {
  logUtil('sendRemoteReduxAction').debug('action: ', action)
  if (action.payload.data) {
    return sendRemoteDataReduxAction(action)
  }
  return sendRemoteInstanceReduxAction(action)
}

let isSendingRequests = false
const MAX_RETRY_COUNT = 1

function sendQueuedRequestsReduxAction () {
  return async (dispatch, getState) => {
    let requests = getRequestsNetworkSelect(getState())
    // logUtil('sendQueuedRequestsReduxActionLogger').debug(`requests: `, requests)

    // If we're already going through the queue, no-op
    // It will it go to the next request once this one is done
    if (isSendingRequests) {
      logUtil('sendQueuedRequestsReduxActionLogger').debug('[noop] already sending request')
      return
    }

    if (!isConnectedNetworkSelect(getState())) {
      logUtil('sendQueuedRequestsReduxActionLogger').debug('[noop] not connected')
      return
    }

    if (!requests.length) {
      logUtil('sendQueuedRequestsReduxActionLogger').debug('[noop] no requests')
      return
    }

    const request = { ...requests[0] }

    // If we're not connected to the server, no-op
    if (!isConnectedNetworkSelect(getState())) {
      // logUtil('sendQueuedRequestsReduxActionLogger').debug('[noop] not connected')
      return
    }

    const { action } = request

    // logUtil('sendQueuedRequestsReduxActionLogger').debug('request: ', request)
    // logUtil('sendQueuedRequestsReduxActionLogger').debug('status: ',
    // getStatusNetworkUtil(request, getState()))

    let isLoadingRequest
    if (getStatusNetworkUtil(request, getState()) === REDUX_STATUS.LOADING) {
      isLoadingRequest = true
      // logUtil('sendQueuedRequestsReduxActionLogger').debug('[noop] loading status, not
      // saving status')
      dispatch(deleteRequestNetworkAction(action))
    }

    isSendingRequests = true

    if (!isLoadingRequest) {
      try {
        // logUtil('sendQueuedRequestsReduxActionLogger').debug('[sending]')
        await dispatch(sendRemoteReduxAction(action))
        // logUtil('sendQueuedRequestsReduxActionLogger').debug('[done]')
        dispatch(deleteRequestNetworkAction(action))
      } catch (error) {
        // logUtil('sendQueuedRequestsReduxActionLogger').error('[error]: ', error)
        // If we're not able to reach the server, increment the retry count
        const message = getMessageErrorUtil(error)
        if (/timeout|network/gi.test(message)) {
          dispatch(incrementRetryCountNetworkAction(action))
        } else {
          // TODO Show the error
          // Remove the request
          dispatch(deleteRequestNetworkAction(action))
        }
        // throw error
      }

      // Update the requests that have been tried too many times
      requests = getRequestsNetworkSelect(getState())
      // logUtil('sendQueuedRequestsReduxActionLogger').debug('[done]: ', requests)
      const filteredRequests = requests.filter(request => request[NETWORK_REQUEST_PATH.RETRY_COUNT] <
        MAX_RETRY_COUNT)
      if (requests.length !== filteredRequests.length) {
        dispatch(setReduxAction({
          type: NETWORK_ACTION.DELETE_FAILED_REQUESTS,
          payload: {
            path: NETWORK_PATH.REQUESTS,
            value: filteredRequests
          }
        }))
      }
    }

    isSendingRequests = false

    // If there is other requests, send them
    requests = getRequestsNetworkSelect(getState())
    if (requests.length) {
      return dispatch(sendQueuedRequestsReduxAction())
    }
  }
}

function hasUnsavedForeignKeys (instance) {
  const data = getUpdatedDataReduxUtil(instance)
  if (!data || isNewReduxUtil(instance)) {
    return false
  }

  // TODO should not happen
  const parentRegexp = /parent/gi
  return Object
    .keys(data)
    .some((key) => {
      const value = data[key]
      return (
        key.endsWith('Id') &&
        !key.match(parentRegexp) &&
        value &&
        isTemporaryIdReduxUtil(value)
      )
    })
}

function isDirectlySavable (instance, state) {
  const className = getClassNameReduxUtil(instance)
  if ([
    EXERCISE_CLASS_NAME,
    WORKOUT_SECTION_CLASS_NAME,
    WORKOUT_EXERCISE_CLASS_NAME
  ].includes(className)) {
    return (
      getProfileIdModelUtil(instance) ===
      getCurrentOrCustomerIdProfileSelect(state) ||
      isAdminProfileSelect(state)
    )
  }

  return true
}

function saveReduxAction (action) {
  return (dispatch, getState) => {
    const { payload } = action
    const {
      path,
      data,
      method,
      endpoint,
      options = {}
    } = payload

    const instance = getObjectUtil(getState(), path)
    if (!endpoint) {
      logUtil('').warn(`[noop] no endpoint was provided for action type: ${action.type}`)
      return
    }

    if (!instance && !data) {
      logUtil('').warn(`[noop] instance at path ${path} does not exist`)
      dispatch(deleteRequestNetworkAction(action))
      return
    }

    // If it's already saving, no-op
    if (!data && path && isSavingReduxSelector(path)(getState())) {
      logUtil('').debug(`[noop] already saving ${path}`)
      return
    }

    if (path &&
      !data &&
      !method &&
      isInstanceReduxUtil(instance) &&
      !options[REDUX_OPTION.IS_FORCED] &&
      !hasChangedReduxUtil(instance)) {
      logUtil('').debug(`[noop] not changed ${path}`)
      return
    }

    // If the instance should not be saved directly, no-op
    // E.g. WorkoutSection, WorkoutExercise
    if (path &&
      !data &&
      isInstanceReduxUtil(instance) &&
      !isDirectlySavable(instance, getState())) {
      logUtil('').debug(`[noop] non savable ${path}`)
      return
    }

    // If the instance has unsaved foreign keys
    if (path &&
      method !==
      'DELETE' &&
      isInstanceReduxUtil(instance) &&
      hasUnsavedForeignKeys(instance)) {
      logUtil('').debug(`[noop] foreign keys unsaved for ${path}: `)
      return
    }

    // If the request can't be queued, do it now
    if (data || options[REDUX_OPTION.IS_IMMEDIATE]) {
      return dispatch(sendRemoteReduxAction(action))
    }

    // Add the request to the queue
    logUtil('').debug(`adding request for ${path}`)
    dispatch(addRequestNetworkAction(action))

    // Throw an error if we require a server response but the user is not
    // connected
    if (!data &&
      !isConnectedNetworkSelect(getState()) &&
      !isInstanceReduxUtil(instance)) {
      logUtil('').warn(`[WARN] connection not available but needed ${path}`)
      dispatch(saveErrorReduxAction({
        type: action.type,
        payload: {
          path,
          error: 'You are not connected to the server. Try again later.'
        }
      }))
      return
    }

    // Send the requests from the queue
    dispatch(sendQueuedRequestsReduxAction())
  }
}

export {
  saveErrorReduxAction,
  sendQueuedRequestsReduxAction
}

export default saveReduxAction
